
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import {
  Category, Lead, PermissionCode, Tag,
} from '@/types';

@Component
export default class EditLeadDetailsDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;

  dialog = false
  valid = false
  loading = false

  update = {
    categories: [],
    tags: [],
    hasSystem: false,
    idTustena: '',
  } as {
    categories: number[];
    tags: number[];
    hasSystem: boolean;
    idTustena: string;
  }

  @Watch('value')
  onValueChange(val: boolean) {
    if (val === true) {
      this.$set(this.update, 'categories', this.leadCategories);
      this.$set(this.update, 'tags', this.leadTags);
      this.$set(this.update, 'hasSystem', this.lead.hasSystem);
      this.$set(this.update, 'idTustena', this.lead.idTustena);
    }
    this.dialog = val;
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  get user() {
    return this.$store.getters.currentUser;
  }

  get userPermissions() {
    return this.$store.getters.currentUserPermissions;
  }

  get lead(): Lead {
    return this.$store.getters.currentLead;
  }

  get isRequester() {
    return this.user && this.lead && this.user.id === this.lead.requesterId;
  }

  get categories(): Category[] {
    return this.$store.getters.categories;
  }

  get visibleCategories() {
    return Array.isArray(this.categories)
      ? this.categories.filter((c) => !c.hidden && c.isActive)
      : [];
  }

  get tags(): Tag[] {
    return this.$store.getters.tags;
  }

  get visibleTags() {
    return Array.isArray(this.tags)
      ? this.tags.filter((t) => !t.hidden && t.isActive)
      : [];
  }

  get flatCategories() {
    return this.lead
      && 'flatCategories' in this.lead
      && typeof this.lead.flatCategories === 'string'
      ? this.lead.flatCategories : null;
  }

  get hasCategories() {
    return this.flatCategories
      && this.flatCategories.length > 2;
  }

  get flatTags() {
    return this.lead
      && 'flatTags' in this.lead
      && typeof this.lead.flatTags === 'string'
      ? this.lead.flatTags : null;
  }

  get hasTags() {
    return this.flatTags
      && this.flatTags.length > 2;
  }

  get leadCategories(): number[] {
    return this.hasCategories && this.flatCategories
      ? this.flatCategories.substring(1, this.flatCategories.length - 1).split(':')
        .map((s: string) => Number(s))
      : [];
  }

  get leadTags(): number[] {
    return this.hasTags && this.flatTags
      ? this.flatTags.substring(1, this.flatTags.length - 1).split(':')
        .map((s: string) => Number(s))
      : [];
  }

  get leadHiddenCategories() {
    return this.leadCategories.filter((cId) => {
      const c = this.leadCategory(cId);
      return c && c.hidden;
    });
  }

  get hasHiddenCategories() {
    return this.leadHiddenCategories.length > 0;
  }

  get leadHiddenTags() {
    return this.leadTags.filter((tId) => {
      const t = this.leadTag(tId);
      return t && t.hidden;
    });
  }

  get hasHiddenTags() {
    return this.leadHiddenTags.length > 0;
  }

  get canModifyCategories() {
    return this.isRequester || this.hasPermission('leads.modify_categories');
  }

  get canModifyTags() {
    return this.hasPermission('leads.modify_tags');
  }

  get form(): Vue & { validate: () => boolean } {
    return this.$refs.form as Vue & { validate: () => boolean };
  }

  hasPermission(codename: PermissionCode) {
    if (!this.user) return false;
    if (this.user.isSuperUser) return true;

    return this.userPermissions.includes(codename);
  }

  leadCategory(categoryId: number) {
    if (!this.categories) return null;

    return this.categories.find((c: Category) => c.id === categoryId);
  }

  leadTag(tagId: number) {
    if (!this.tags) return null;

    return this.tags.find((tag) => tag.id === tagId);
  }

  tagColor(tagId: number): string {
    const tag = this.leadTag(tagId);

    const baseColor = tag?.color ? tag.color : 'light-blue';

    // Se il tag è nascosto o disattivato, schiarisce il colore
    if (tag?.hidden || tag?.isActive === false) return `${baseColor} lighten-3`;

    return baseColor;
  }

  removeCategory(category: Category) {
    console.log('EditLeadDetailsDialog: removeCategory() category =', category); // eslint-disable-line no-console

    const newCategories = this.update.categories.filter((categoryId) => categoryId !== category.id);

    this.$set(this.update, 'categories', newCategories);
  }

  removeTag(tag: Tag) {
    console.log('EditLeadDetailsDialog: removeTag() tag =', tag); // eslint-disable-line no-console

    const newTags = this.update.tags.filter((tagId) => tagId !== tag.id);

    this.$set(this.update, 'tags', newTags);
  }

  onCancel() {
    this.dialog = false;
  }

  async onUpdate() {
    if (this.form.validate()) {
      this.loading = true;

      await this.$store.dispatch('updateLead', {
        updateType: 'details',
        update: this.update,
      });

      this.dialog = false;
      setTimeout(() => {
        this.loading = false;
      }, 500);
    }
  }
}
